// What the phone preview must agree with: each of these mirrors the mobile
// AnnouncementCard (apps/mobile/src/components/AnnouncementCard.tsx). Keep the two in step,
// or the admin approves something the employee never sees.

/**
 * Mirrors formatRemaining() in the mobile AnnouncementCard — keep the two in
 * step, or the preview promises a different pill than the card renders.
 *
 * A DURATION, not a date name: `2d 19h 32m 15s`, with leading zero units
 * trimmed. The preview ticks once a second, so what the admin sees here
 * is exactly what the employee's card does.
 */
const formatRemaining = (ms: number): string => {
  if (ms <= 0) return "It's here!";
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${h}h ${m}m ${s}s`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

export const countdownPreview = (iso: string): string | null => {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  return formatRemaining(target.getTime() - Date.now());
};

/**
 * Mirrors remainingSegments() in the mobile AnnouncementCard — the card renders
 * the countdown as labelled blocks, so a preview showing a run-on string would
 * be promising the wrong thing.
 */
export const previewSegments = (iso: string): { value: string; label: string }[] | null => {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  const ms = target.getTime() - Date.now();
  if (ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const all = [
    { value: pad(d), label: d === 1 ? 'DAY' : 'DAYS', keep: d > 0 },
    { value: pad(h), label: 'HRS', keep: d > 0 || h > 0 },
    { value: pad(m), label: 'MIN', keep: d > 0 || h > 0 || m > 0 },
    { value: pad(s), label: 'SEC', keep: true },
  ];
  const first = all.findIndex((u) => u.keep);
  return all.slice(first === -1 ? all.length - 1 : first);
};

/** #RRGGBB → rgba, matching the card's accent tint on mobile. */
export const tint = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Mirrors readableAccent() in the mobile AnnouncementCard. The accent doubles
 * as text on a near-white card, and the custom picker allows any hex — so pull
 * the lightness back once it's too pale to read, preserving hue. Preview and
 * card must agree, or the admin approves something the employee can't read.
 */
export const readableAccent = (hex: string): string => {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  const CEILING = 0.55;
  if (luminance <= CEILING) return hex;
  const factor = Math.max(0.35, CEILING / luminance);
  const clamp = (v: number) => Math.round(Math.min(255, Math.max(0, v * factor)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`;
};

/**
 * Mirrors labelOnFill() in the mobile AnnouncementCard: the task button's label
 * is white or ink, whichever contrasts more with the (readable) accent fill.
 */
export const labelOnFill = (fill: string): string => {
  if (!/^#[0-9a-fA-F]{6}$/.test(fill)) return '#FFFFFF';
  const luminance = (hex: string) => {
    const channel = (i: number) => {
      const c = parseInt(hex.slice(i, i + 2), 16) / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
  };
  const INK = '#0F172A';
  const l = luminance(fill);
  const vsWhite = 1.05 / (l + 0.05);
  const vsInk = (l + 0.05) / (luminance(INK) + 0.05);
  return vsWhite >= vsInk ? '#FFFFFF' : INK;
};
